import { Link } from '@tanstack/react-router';
import { AlertTriangle, Clock } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import { cn, formatDate } from '@/lib/utils';

/**
 * באנרי מנוי מעל התוכן, מבלוק החיוב שבסשן (/auth/me):
 * - קריאה בלבד — לכולם (בעלים: קישור לבחירת תוכנית / הסדרת תשלום; אחרים: לפנות לבעלים).
 * - תשלום שנכשל, בתוך תקופת החסד — לבעלים.
 * - ניסיון שנגמר בעוד שבוע או פחות — לבעלים.
 * עסק המפעיל פטור בשרת (readOnly=false), ולכן לא צריך בדיקה כאן.
 */
export function BillingBanner() {
  const { t } = useTranslation('billing');
  const { session } = useAuth();
  const billing = session?.billing;
  if (!session || !billing) return null;
  const isOwner = session.user.role === 'OWNER';

  if (billing.readOnly) {
    const reason = billing.reason ?? 'generic';
    return (
      <Banner tone="danger" icon={<AlertTriangle className="size-4 shrink-0" aria-hidden />}>
        <span>
          {t(`banner.readOnly.${reason}`)} {t('banner.dataKept')}{' '}
          {isOwner ? null : t('banner.askOwner')}
        </span>
        {isOwner ? (
          <BillingLink>
            {billing.reason === 'PAST_DUE' ? t('banner.settlePayment') : t('banner.choosePlan')}
          </BillingLink>
        ) : null}
      </Banner>
    );
  }

  if (!isOwner) return null;

  if (billing.status === 'PAST_DUE') {
    return (
      <Banner tone="warning" icon={<AlertTriangle className="size-4 shrink-0" aria-hidden />}>
        <span>{t('banner.pastDue', { date: formatDate(billing.graceEndsAt) })}</span>
        <BillingLink>{t('banner.settlePayment')}</BillingLink>
      </Banner>
    );
  }

  if (
    billing.status === 'TRIALING' &&
    billing.trialDaysLeft !== null &&
    billing.trialDaysLeft <= 7
  ) {
    return (
      <Banner tone="info" icon={<Clock className="size-4 shrink-0" aria-hidden />}>
        <span>
          {billing.trialDaysLeft <= 0
            ? t('banner.trialEndsToday')
            : t('banner.trialEnding', { count: billing.trialDaysLeft })}
        </span>
        <BillingLink>{t('banner.choosePlan')}</BillingLink>
      </Banner>
    );
  }

  return null;
}

function Banner({
  tone,
  icon,
  children,
}: {
  tone: 'danger' | 'warning' | 'info';
  icon: ReactNode;
  children: ReactNode;
}) {
  const cls = {
    danger: 'border-danger-border bg-danger-subtle text-danger',
    warning: 'border-warning-border bg-warning-subtle text-warning',
    info: 'border-info-border bg-info-subtle text-info',
  }[tone];
  return (
    <div
      role="status"
      className={cn(
        'flex flex-wrap items-center gap-x-3 gap-y-1 border-b px-4 py-2.5 text-xs font-medium lg:px-6',
        cls,
      )}
    >
      {icon}
      {children}
    </div>
  );
}

function BillingLink({ children }: { children: ReactNode }) {
  return (
    <Link
      to="/settings/$tab"
      params={{ tab: 'billing' }}
      className="font-semibold underline underline-offset-4 hover:no-underline"
    >
      {children}
    </Link>
  );
}
